export const samlConfigTableHeader = [
  {
    name: "Connector",
    field: "connector",
    align: "left",
    sorting: false,
  },
  {
    name: "Email Domains",
    field: "domains",
    align: "left",
    sorting: false,
  },
  {
    name: "IdP Source",
    field: "source",
    align: "left",
    sorting: false,
  },
  {
    name: "Login Policy",
    field: "policy",
    align: "left",
    sorting: false,
  },
  {
    name: "Status",
    field: "status",
    align: "left",
    sorting: false,
  },
  {
    name: "Action",
    field: "action",
    align: "right",
    sorting: false,
    hide: true,
  },
];

export const MAX_SLUG_LENGTH = 60;

const DOMAIN_REGEX =
  /^(?=.{1,253}$)(?!-)[A-Za-z0-9-]{1,63}(?:\.[A-Za-z0-9-]{1,63})+$/;

export const normalizeSlug = (raw: string): string =>
  raw
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, MAX_SLUG_LENGTH);

export const validateSlug = (input: string): string => {
  const value = input.trim();
  if (!value) return "";
  if (value.length > MAX_SLUG_LENGTH)
    return `Connector name must be ${MAX_SLUG_LENGTH} characters or fewer`;
  if (!normalizeSlug(value))
    return "Use at least one letter or number";
  return "";
};

export const validateEmailDomain = (input: string): string => {
  const value = input.trim().toLowerCase();
  if (!value) return "Enter a domain";
  if (!DOMAIN_REGEX.test(value))
    return "Enter a valid domain, e.g. acme.com";
  return "";
};

const isHttpUrl = (value: string): boolean => {
  try {
    const url = new URL(value.trim());
    return url.protocol === "http:" || url.protocol === "https:";
  } catch {
    return false;
  }
};

export interface SamlValidationInput {
  mode: "metadata" | "manual";
  emailDomains: string[];
  metadataUrl: string;
  idpEntityId: string;
  idpSsoUrl: string;
  idpVerificationCertificate: string;
  idpSloUrl?: string;
}

export interface SamlValidationErrors {
  emailDomains?: string;
  metadataUrl?: string;
  idpEntityId?: string;
  idpSsoUrl?: string;
  idpVerificationCertificate?: string;
  idpSloUrl?: string;
}

export const validateSamlConfig = (
  input: SamlValidationInput,
): SamlValidationErrors => {
  const errors: SamlValidationErrors = {};

  if (!input.emailDomains.length) {
    errors.emailDomains = "Add at least one email domain";
  }

  if (input.mode === "metadata") {
    const url = input.metadataUrl.trim();
    if (!url) errors.metadataUrl = "Enter the IdP metadata URL";
    else if (!isHttpUrl(url))
      errors.metadataUrl = "Enter a valid http(s) URL";
  } else {
    if (!input.idpEntityId.trim())
      errors.idpEntityId = "Enter the IdP entity ID / issuer";
    if (!input.idpSsoUrl.trim())
      errors.idpSsoUrl = "Enter the IdP SSO URL";
    else if (!isHttpUrl(input.idpSsoUrl))
      errors.idpSsoUrl = "Enter a valid http(s) URL";
    if (!input.idpVerificationCertificate.trim())
      errors.idpVerificationCertificate =
        "Paste the IdP signing certificate (PEM)";
    if (input.idpSloUrl?.trim() && !isHttpUrl(input.idpSloUrl))
      errors.idpSloUrl = "Enter a valid http(s) URL";
  }

  return errors;
};

export interface OidcValidationInput {
  mode: "issuer" | "manual";
  emailDomains: string[];
  issuer: string;
  authorizationUri: string;
  tokenUri: string;
  userInfoUri: string;
  jwkSetUri: string;
  endSessionUri?: string;
  clientId: string;
  clientSecret: string;
  /** True when editing a connector that already has a client secret on file — lets it be left blank. */
  hasStoredClientSecret: boolean;
  clientAuthenticationMethod: string;
  scopes: string[];
}

export interface OidcValidationErrors {
  emailDomains?: string;
  issuer?: string;
  authorizationUri?: string;
  tokenUri?: string;
  userInfoUri?: string;
  jwkSetUri?: string;
  endSessionUri?: string;
  clientId?: string;
  clientSecret?: string;
  clientAuthenticationMethod?: string;
  scopes?: string;
}

export const validateOidcConfig = (
  input: OidcValidationInput,
): OidcValidationErrors => {
  const errors: OidcValidationErrors = {};

  if (!input.emailDomains.length) {
    errors.emailDomains = "Add at least one email domain";
  }

  if (input.mode === "issuer") {
    const url = input.issuer.trim();
    if (!url) errors.issuer = "Enter the IdP issuer URL";
    else if (!isHttpUrl(url)) errors.issuer = "Enter a valid http(s) URL";
  } else {
    if (!input.authorizationUri.trim())
      errors.authorizationUri = "Enter the authorization endpoint";
    else if (!isHttpUrl(input.authorizationUri))
      errors.authorizationUri = "Enter a valid http(s) URL";
    if (!input.tokenUri.trim()) errors.tokenUri = "Enter the token endpoint";
    else if (!isHttpUrl(input.tokenUri))
      errors.tokenUri = "Enter a valid http(s) URL";
    if (!input.userInfoUri.trim())
      errors.userInfoUri = "Enter the user info endpoint";
    else if (!isHttpUrl(input.userInfoUri))
      errors.userInfoUri = "Enter a valid http(s) URL";
    if (!input.jwkSetUri.trim())
      errors.jwkSetUri = "Enter the JWK set endpoint";
    else if (!isHttpUrl(input.jwkSetUri))
      errors.jwkSetUri = "Enter a valid http(s) URL";
  }

  if (input.endSessionUri?.trim() && !isHttpUrl(input.endSessionUri))
    errors.endSessionUri = "Enter a valid http(s) URL";

  if (!input.clientId.trim()) errors.clientId = "Enter the client ID";

  if (!input.clientSecret.trim() && !input.hasStoredClientSecret)
    errors.clientSecret = "Enter the client secret";

  if (!input.clientAuthenticationMethod)
    errors.clientAuthenticationMethod =
      "Select a client authentication method";

  if (!input.scopes.includes("openid"))
    errors.scopes = 'Scopes must include "openid"';

  return errors;
};

export const hasErrors = (
  errors: SamlValidationErrors | OidcValidationErrors,
): boolean => Object.values(errors).some(Boolean);
